// pages/payout.ts — IRS §4942 Funding Target Calculator.
//
// Mirrors frontend2/Payout.jsx :: PayoutPage. Pure client-side math —
// a ballpark of the foundation's required annual distribution.
//
//   • Four expandable inputs (FMV, NII, carryover, distYtd) with
//     step-by-step "How do I find this number?" walkthroughs.
//   • Result block (big number + ±$5k band + pacing bar) updates live.
//   • Inputs persist server-side per fiscal year via api.getPayout/savePayout.
//   • localStorage key bff.payout.inputs is also written as a soft cache
//     (preserved from the handoff — handy when the API is offline).
//
// Role-gated to approver + admin.

import type { Page, PageContext } from '../router';
import { html, esc, fmtRelative } from '../util';

interface PayoutInputs { fmv: number; nii: number; carryover: number; distYtd: number; }
interface PayoutRow extends PayoutInputs { updatedAt?: string; updatedByName?: string | null; }

const PAYOUT_DEFAULTS: PayoutInputs = { fmv: 5000000, nii: 150000, carryover: 8000, distYtd: 15000 };
const LS_KEY = 'bff.payout.inputs';

const fmt = (n: number): string => '$' + Math.round(n).toLocaleString('en-US');

function inputsEqual(a: PayoutInputs | null, b: PayoutInputs | null): boolean {
  if (!a || !b) return false;
  return a.fmv === b.fmv && a.nii === b.nii && a.carryover === b.carryover && a.distYtd === b.distYtd;
}

function readCachedInputs(): PayoutInputs | null {
  try {
    const raw = localStorage.getItem(LS_KEY); if (!raw) return null;
    const j = JSON.parse(raw) as Partial<PayoutInputs>;
    if (typeof j.fmv !== 'number' || typeof j.nii !== 'number'
        || typeof j.carryover !== 'number' || typeof j.distYtd !== 'number') return null;
    return j as PayoutInputs;
  } catch { return null; }
}
function writeCachedInputs(p: PayoutInputs): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

// ─── Step-line helper (verbatim copy from the React source) ──────
function stepLine(n: number | string, body: string): string {
  return `
    <div style="display: grid; grid-template-columns: 24px 1fr; gap: 10px; padding: 4px 0;">
      <span class="serif" style="color: var(--primary); font-size: 15px; line-height: 1.4;">${n}.</span>
      <div>${body}</div>
    </div>
  `;
}
function kw(s: string): string {
  return `<span style="padding: 1px 6px; border-radius: 4px; background: var(--card);
    border: 1px solid var(--rule); font-size: 12.5px; color: var(--ink);">${esc(s)}</span>`;
}

// ─── HELP content (large strings) ─────────────────────────────────
const HELP_FMV = `
  <p style="margin: 0 0 12px;">
    You need the <b>average fair-market value (FMV)</b> of the brokerage account across all twelve months of your fiscal year (Nov 1 – Oct 31).
  </p>
  ${stepLine(1, `Pull the <b>twelve month-end statements</b> from Schwab/Fidelity for Nov of the prior year through Oct of this year.`)}
  ${stepLine(2, `Note the ${kw('Total Account Value')} on the last page of each statement — sometimes labeled ${kw('Net Asset Value')} or ${kw('Ending Balance')}.`)}
  ${stepLine(3, `Add the twelve numbers together and divide by 12. That's your average FMV.`)}
  ${stepLine(4, `If the foundation holds any non-investment property used to produce income (rental real estate, etc.), add its average FMV too. <b>Bray foundation: skip — you only hold brokerage assets.</b>`)}
  <p style="margin: 14px 0 0; padding: 10px 12px; background: var(--primary-soft); border-radius: 8px; color: var(--ink); font-size: 13px;">
    <b>Shortcut for our foundation:</b> since 100% of income comes from capital appreciation in the brokerage, FMV equals the brokerage account's ${kw('Total Account Value')}. The Schwab year-end report includes a "twelve-month average balance" line on page 1 — that's your number.
  </p>
`;

const HELP_NII = `
  <p style="margin: 0 0 12px;">
    Net investment income is everything the brokerage <i>actually earned</i> this fiscal year — separate from unrealized gains that just sit on paper.
  </p>
  ${stepLine(1, `Open the brokerage's <b>Year-End Tax Reporting Package</b> for the period Nov 1 – Oct 31. (Schwab calls this the "Tax & Cost Basis Summary.")`)}
  ${stepLine(2, `Add these three lines together:
    <ul style="margin: 8px 0 0; padding-left: 18px;">
      <li>${kw('Realized capital gains (net)')} — from sales of securities during the year</li>
      <li>${kw('Dividends received')} (ordinary + qualified)</li>
      <li>${kw('Interest income')} — money-market interest, bond coupons</li>
    </ul>`)}
  ${stepLine(3, `Subtract any <b>direct investment expenses</b> — the brokerage's management fee, custody fee, advisor fee. (Not the foundation's grant-admin costs.)`)}
  <p style="margin: 14px 0 0; padding: 10px 12px; background: var(--primary-soft); border-radius: 8px; color: var(--ink); font-size: 13px;">
    <b>Why it matters:</b> the IRS levies a flat ${kw('1.39%')} excise tax on this number (Section 4940). It's a small bite — for $150K of NII, that's about $2,085 — but it reduces the amount you're required to give away. <br/><br/>
    <b>Heads-up:</b> unrealized appreciation (paper gains the brokerage <i>has</i> but hasn't sold) does <b>not</b> belong here. Only realized gains do.
  </p>
`;

const HELP_CARRY = `
  <p style="margin: 0 0 12px;">
    "Excess distribution carryover" is a credit you earn whenever you give more than the required minimum in a year. The IRS lets you bank up to five years of that excess and apply it against future requirements.
  </p>
  ${stepLine(1, `Pull the foundation's ${kw('Form 990-PF')} from the prior tax year.`)}
  ${stepLine(2, `Find ${kw('Part XII — Undistributed Income')} (sometimes labeled Part XIII depending on the form year).`)}
  ${stepLine(3, `The bottom of that section shows the running balance of unused excess by vintage year. The total is your carryover.`)}
  ${stepLine(4, `If the foundation has never exceeded the minimum, this is ${kw('$0')}. If unsure, leave it at ${kw('$0')} for the ballpark — your CPA will plug in the real number.`)}
  <p style="margin: 14px 0 0; padding: 10px 12px; background: var(--accent-soft); border-radius: 8px; color: var(--ink-2); font-size: 13px;">
    <b>Practical note:</b> the carryover ages off after 5 years. If you've been generous lately and are carrying a balance, it's worth knowing — that's why it shows up here. But the only way to know the exact figure is the prior 990-PF.
  </p>
`;

const HELP_YTD = `
  <p style="margin: 0 0 12px;">
    This is the simplest input — it's just <b>everything you've given away</b> during the current fiscal year (Nov 1 to today).
  </p>
  ${stepLine(1, `Open the foundation's grant register / spreadsheet.`)}
  ${stepLine(2, `Sum all grant cheques <b>cleared</b> (or wired) between Nov 1 of last year and today.`)}
  ${stepLine(3, `Add directly-related administrative expenses: postage and printing for grant correspondence, software fees for this portal, accountant fees specifically for grant administration. <b>Don't</b> include the brokerage's management fee — that's an investment expense, not a qualifying distribution.`)}
  <p style="margin: 14px 0 0; padding: 10px 12px; background: var(--primary-soft); border-radius: 8px; color: var(--ink); font-size: 13px;">
    <b>Shortcut for our foundation:</b> the Grants table on the dashboard has a running total. That's almost the same number — add maybe $500–$2,000 of admin overhead and you're there.
  </p>
`;

// ─── Expandable input renderer ────────────────────────────────────
function expandableInput(opts: { key: keyof PayoutInputs; label: string; value: number; help: string; details: string; }): string {
  const display = opts.value === 0 ? '0' : Number(opts.value).toLocaleString('en-US');
  return html`
    <div class="po-input" data-input-key="${opts.key}">
      <label class="label" style="display: block;">${opts.label}</label>
      <div style="display: flex; align-items: center; gap: 8px;
                  background: var(--card); border: 1px solid var(--rule-2);
                  border-radius: 12px; padding: 12px 16px; margin-top: 8px;">
        <span class="t-small" style="color: var(--ink-soft); font-size: 15px;">$</span>
        <input type="text" inputmode="numeric" data-input-value="${opts.key}"
               value="${display}"
               style="border: none; outline: none; background: transparent;
                      font-family: var(--ui); font-size: 18px; color: var(--ink);
                      width: 100%; padding: 0; font-variant-numeric: tabular-nums;"/>
      </div>
      <p class="t-small" style="margin: 8px 0 0; font-size: 12.5px; line-height: 1.55; color: var(--ink-soft);">
        ${opts.help}
      </p>
      <button type="button" data-action="toggle-help" data-help-key="${opts.key}"
              style="align-self: flex-start; background: transparent; border: none; cursor: pointer;
                     color: var(--primary); font-size: 12.5px; font-weight: 500;
                     padding: 4px 0; margin-top: 8px;
                     display: inline-flex; align-items: center; gap: 6px;">
        <span data-help-icon="${opts.key}"
              style="display: inline-grid; place-items: center; width: 14px; height: 14px;
                     border-radius: 999px; border: 1px solid var(--primary);
                     font-size: 11px; line-height: 1; font-weight: 600;">+</span>
        <span data-help-label="${opts.key}">How do I find this number?</span>
      </button>
      <div data-help-body="${opts.key}"
           style="max-height: 0; overflow: hidden; transition: max-height 400ms ease;">
        <div style="background: var(--paper-2); border: 1px solid var(--rule);
                    border-radius: 12px; padding: 18px 20px; margin-top: 8px;
                    font-size: 13.5px; line-height: 1.65; color: var(--ink-2);">
          ${{ __raw: opts.details }}
        </div>
      </div>
    </div>
  `;
}

// ─── Calc-row renderer ────────────────────────────────────────────
function calcRow(label: string, sub: string | null, value: string, emphasis?: 'minus' | 'result', sign?: 'minus'): string {
  const color = emphasis === 'minus' ? 'var(--st-action)'
              : emphasis === 'result' ? 'var(--primary)'
              : 'var(--ink)';
  const size = emphasis === 'result' ? '28px' : '20px';
  return `
    <div class="d-flex" style="padding: 14px 0; border-top: 1px solid var(--rule);
                                align-items: baseline; gap: 16px;">
      <div style="flex: 1;">
        <div style="font-size: 15px;">${esc(label)}</div>
        ${sub ? `<div class="t-small" style="margin-top: 4px; font-size: 12.5px;">${esc(sub)}</div>` : ''}
      </div>
      <div class="serif" style="font-size: ${size}; color: ${color}; font-variant-numeric: tabular-nums;">
        ${sign === 'minus' ? '\u2212 ' : ''}${esc(value)}
      </div>
    </div>
  `;
}

// ─── Page ─────────────────────────────────────────────────────────

const PayoutPage: Page = {
  guard(ctx: PageContext): string | null {
    if (!ctx.user) return 'login';
    if (ctx.userRole !== 'admin' && ctx.userRole !== 'approver') return 'landing';
    return null;
  },

  render(_ctx: PageContext): string {
    const today = new Date();
    const fyStart = new Date(today.getMonth() >= 10 ? today.getFullYear() : today.getFullYear() - 1, 10, 1);
    const fyEnd   = new Date(fyStart.getFullYear() + 1, 9, 31);
    const fyLabel = `Nov ${String(fyStart.getFullYear()).slice(-2)} – Oct ${String(fyEnd.getFullYear()).slice(-2)}`;
    void fyEnd;

    return html`
      <main class="page" style="background: var(--paper-2); min-height: 100vh;">
        <section style="padding: 48px 0 16px;">
          <div class="wrap" style="max-width: 1200px;">
            <div class="eyebrow">Annual payout · Fiscal year ${fyLabel}</div>
            <h1 class="t-h1" style="margin: 12px 0 0; font-size: clamp(40px, 5vw, 64px);">
              How much do we need to give away?
            </h1>
            <p class="t-lede" style="margin: 16px 0 0; max-width: 680px;">
              A ballpark of the IRS minimum distribution under Section 4942 — accurate within about $5,000.
              Each input has an expandable walkthrough; your CPA still closes the books on the 990-PF.
            </p>
          </div>
        </section>

        <section style="padding: 40px 0 96px;">
          <div class="wrap po-grid" style="max-width: 1200px;">
            <!-- INPUTS -->
            <div class="card po-inputs" style="padding: 32px;">
              <h2 class="t-h3 serif" style="margin: 0 0 4px;">What we know.</h2>
              <p class="t-small" style="margin: 0 0 28px; font-size: 13px;">
                Edit any number — the target on the right updates live.
                Click "How do I find this number?" under each field for a step-by-step.
                Press <b>Save</b> to share these inputs with the rest of the board.
              </p>

              <div id="po-inputs-list" style="display: flex; flex-direction: column; gap: 32px;"></div>

              <div style="display: flex; flex-direction: column; gap: 8px;
                          margin-top: 28px; padding-top: 24px; border-top: 1px solid var(--rule);">
                <button type="button" id="po-save-btn" class="btn btn-primary"
                        style="align-self: stretch;" disabled>Save</button>
                <div id="po-save-status" class="t-small" style="font-size: 12.5px; color: var(--ink-soft);"></div>
              </div>
            </div>

            <!-- RESULT -->
            <div class="po-result" style="display: flex; flex-direction: column; gap: 24px;">
              <div class="card" style="padding: 40px;">
                <div class="d-flex" style="justify-content: space-between; align-items: baseline;">
                  <div class="label">Target qualifying distribution · ${fyLabel}</div>
                  <div class="t-small">±$5,000</div>
                </div>
                <div id="po-target" class="serif"
                     style="font-size: 88px; line-height: 1; color: var(--primary);
                            margin: 24px 0 8px; letter-spacing: -0.02em; font-variant-numeric: tabular-nums;">
                  $0
                </div>
                <div id="po-range" class="t-small" style="font-size: 14px; color: var(--ink-soft);"></div>

                <div style="margin-top: 32px;">
                  <div class="d-flex" style="justify-content: space-between; align-items: baseline; margin-bottom: 10px;">
                    <div style="font-size: 14.5px;">
                      <span id="po-distYtd-label" class="serif" style="font-size: 20px; color: var(--primary);">$0</span>
                      <span class="t-small"> distributed of <span id="po-required-label">$0</span></span>
                    </div>
                    <div class="t-small" id="po-pct-label">0%</div>
                  </div>
                  <div style="width: 100%; height: 8px; border-radius: 999px;
                              background: var(--paper-3); position: relative; overflow: hidden;">
                    <div id="po-bar" style="width: 0%; height: 100%; background: var(--primary);
                                            transition: width 400ms ease;"></div>
                    <div id="po-fy-marker" style="position: absolute; top: -3px; bottom: -3px;
                                                  width: 2px; left: 0%; background: var(--ink-2);"></div>
                  </div>
                  <div class="d-flex" style="justify-content: space-between; margin-top: 8px;
                                             font-size: 12px; color: var(--ink-soft);">
                    <span id="po-pace-label"></span>
                    <span id="po-fy-label"></span>
                  </div>
                </div>
              </div>

              <div class="card" style="padding: 32px 36px;">
                <div class="d-flex" style="justify-content: space-between; align-items: baseline; margin-bottom: 16px;">
                  <h2 class="t-h3 serif" style="margin: 0;">How we got there.</h2>
                  <div class="t-small">Section 4942 · simplified</div>
                </div>
                <div id="po-breakdown"></div>
              </div>

              <div style="padding: 20px 24px; border-radius: 14px; background: var(--accent-soft);
                          color: var(--ink-2); display: grid; grid-template-columns: auto 1fr;
                          gap: 16px; align-items: start;">
                <div style="width: 28px; height: 28px; border-radius: 999px; background: var(--accent);
                            color: var(--paper); display: grid; place-items: center;
                            font-family: var(--display); font-size: 16px; font-weight: 500;">!</div>
                <div style="font-size: 14.5px; line-height: 1.6;">
                  <b>This is a ballpark, not the final number.</b> Real 990-PF reporting involves
                  month-by-month FMV averaging, treatment of dual-use assets, qualifying-distribution
                  detail, and prior carryover sequencing. Use this to plan; let your CPA close the books.
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    `;
  },

  mount(root: HTMLElement, _ctx: PageContext): void {
    let alive = true;
    let inputs: PayoutInputs = { ...PAYOUT_DEFAULTS, ...(readCachedInputs() || {}) };
    let lastSaved: PayoutRow | null = null;
    let loadState: 'loading' | 'ready' | 'error' = 'loading';
    let saveState: 'idle' | 'saving' | 'error' = 'idle';
    let errMsg = '';

    // Fiscal year computations (mirror React)
    const today = new Date();
    const fyStart = new Date(today.getMonth() >= 10 ? today.getFullYear() : today.getFullYear() - 1, 10, 1);
    const fyEnd   = new Date(fyStart.getFullYear() + 1, 9, 31);
    const fiscalYearStart = fyStart.getFullYear();
    const pct     = Math.max(0, Math.min(1, (today.getTime() - fyStart.getTime()) / (fyEnd.getTime() - fyStart.getTime())));
    const fyLabel = `Nov ${String(fyStart.getFullYear()).slice(-2)} – Oct ${String(fyEnd.getFullYear()).slice(-2)}`;

    const inputsListEl = root.querySelector('#po-inputs-list')   as HTMLElement | null;
    const targetEl     = root.querySelector('#po-target')        as HTMLElement | null;
    const rangeEl      = root.querySelector('#po-range')         as HTMLElement | null;
    const distLabel    = root.querySelector('#po-distYtd-label') as HTMLElement | null;
    const reqLabel     = root.querySelector('#po-required-label') as HTMLElement | null;
    const pctLabel     = root.querySelector('#po-pct-label')     as HTMLElement | null;
    const barEl        = root.querySelector('#po-bar')           as HTMLElement | null;
    const fyMarker     = root.querySelector('#po-fy-marker')     as HTMLElement | null;
    const paceLabel    = root.querySelector('#po-pace-label')    as HTMLElement | null;
    const fyLabelEl    = root.querySelector('#po-fy-label')      as HTMLElement | null;
    const breakdownEl  = root.querySelector('#po-breakdown')     as HTMLElement | null;
    const saveBtn      = root.querySelector('#po-save-btn')      as HTMLButtonElement | null;
    const saveStatusEl = root.querySelector('#po-save-status')   as HTMLElement | null;

    function calc(p: PayoutInputs): {
      reduction: number; adjustedBase: number; mir: number; tax: number;
      distributable: number; required: number; gap: number; low: number; high: number;
    } {
      const reduction     = p.fmv * 0.015;
      const adjustedBase  = p.fmv - reduction;
      const mir           = adjustedBase * 0.05;
      const tax           = p.nii * 0.0139;
      const distributable = mir - tax;
      const required      = Math.max(0, distributable - p.carryover);
      const gap           = Math.max(0, required - p.distYtd);
      return {
        reduction, adjustedBase, mir, tax, distributable, required, gap,
        low: Math.max(0, required - 5000), high: required + 5000,
      };
    }

    function paintInputs(): void {
      if (!inputsListEl) return;
      inputsListEl.innerHTML =
        expandableInput({ key: 'fmv',       label: `Average investment assets · ${fyLabel}`,
          value: inputs.fmv,
          help: 'Twelve-month average FMV of the brokerage account. For our foundation, this is the Total Account Value averaged across the fiscal year.',
          details: HELP_FMV }) +
        expandableInput({ key: 'nii',       label: `Net investment income · ${fyLabel}`,
          value: inputs.nii,
          help: 'Realized gains + dividends + interest, minus brokerage fees. Pull from the year-end tax report. Used to compute the 1.39% Section 4940 tax.',
          details: HELP_NII }) +
        expandableInput({ key: 'carryover', label: 'Excess distribution carryover',
          value: inputs.carryover,
          help: 'Unused excess giving from the last five years. Pull from the prior 990-PF, Part XII. If unsure, leave at $0.',
          details: HELP_CARRY }) +
        expandableInput({ key: 'distYtd',   label: 'Distributed year-to-date',
          value: inputs.distYtd,
          help: 'Grants paid since Nov 1, plus direct grant-admin expenses. Since we track giving to the dollar, paste the running total.',
          details: HELP_YTD });
    }

    function paintResult(): void {
      const c = calc(inputs);
      const onTrack = c.gap === 0;
      const ratio   = Math.min(100, (inputs.distYtd / Math.max(1, c.required)) * 100);
      const pacingTarget = c.required * pct;
      const pacing = inputs.distYtd >= pacingTarget ? 'on pace'
                   : inputs.distYtd >= pacingTarget * 0.7 ? 'slightly behind'
                   : 'behind pace';

      if (targetEl) targetEl.textContent = fmt(c.required);
      if (rangeEl)  rangeEl.innerHTML = `Likely between <b style="color: var(--ink);">${fmt(c.low)}</b> and <b style="color: var(--ink);">${fmt(c.high)}</b>.`;
      if (distLabel) {
        distLabel.textContent = fmt(inputs.distYtd);
        distLabel.style.color = onTrack ? 'var(--primary)' : 'var(--st-action)';
      }
      if (reqLabel)  reqLabel.textContent = fmt(c.required);
      if (pctLabel)  pctLabel.textContent = Math.round(ratio) + '%';
      if (barEl) {
        barEl.style.width = ratio + '%';
        barEl.style.background = onTrack ? 'var(--primary)' : 'var(--accent)';
      }
      if (fyMarker) fyMarker.style.left = Math.min(100, pct * 100) + '%';
      if (paceLabel) {
        paceLabel.innerHTML = onTrack
          ? 'On track — minimum already met.'
          : `Still to give: <b style="color: var(--ink);">${fmt(c.gap)}</b> · pacing <b style="color: var(--ink);">${pacing}</b>`;
      }
      if (fyLabelEl) fyLabelEl.textContent = `Fiscal year ${Math.round(pct * 100)}% complete`;
      if (breakdownEl) {
        breakdownEl.innerHTML =
          calcRow('Average investment assets',                'Your input',                                                fmt(inputs.fmv)) +
          calcRow('Less: 1.5% cash held for charitable use',  'Standard reduction — Reg §53.4942(a)-2(c)(4)',              fmt(c.reduction), 'minus', 'minus') +
          calcRow('Adjusted asset base',                      null,                                                        fmt(c.adjustedBase)) +
          calcRow('× 5% minimum investment return',           'The required annual payout rate',                            fmt(c.mir)) +
          calcRow('Less: Section 4940 excise tax',            `1.39% on net investment income of ${fmt(inputs.nii)}`,       fmt(c.tax), 'minus', 'minus') +
          calcRow('Distributable amount',                     null,                                                        fmt(c.distributable)) +
          calcRow('Less: prior-year excess carryover',        'Excess giving from the last 5 years',                        fmt(inputs.carryover), 'minus', 'minus') +
          calcRow('Required distribution this year',          null,                                                        fmt(c.required), 'result');
      }
    }

    function paintSaveControls(): void {
      const dirty = !inputsEqual(inputs, lastSaved);
      const blocked = loadState !== 'ready' || saveState === 'saving' || !dirty;
      if (saveBtn) {
        saveBtn.textContent = saveState === 'saving' ? 'Saving…' : (dirty ? 'Save' : 'Saved');
        saveBtn.disabled = blocked;
        saveBtn.style.opacity = blocked ? '0.5' : '1';
        saveBtn.style.cursor  = blocked ? 'not-allowed' : 'pointer';
      }
      if (saveStatusEl) {
        if (errMsg) {
          saveStatusEl.style.color = 'var(--st-action)';
          saveStatusEl.textContent = errMsg;
        } else if (lastSaved) {
          saveStatusEl.style.color = 'var(--ink-soft)';
          saveStatusEl.textContent = `Last saved ${fmtRelative(lastSaved.updatedAt)}` +
            (lastSaved.updatedByName ? ` by ${lastSaved.updatedByName}` : '') + '.';
        } else if (loadState === 'ready') {
          saveStatusEl.style.color = 'var(--ink-soft)';
          saveStatusEl.textContent = 'No saved inputs for this fiscal year yet — showing defaults.';
        } else {
          saveStatusEl.textContent = '';
        }
      }
    }

    function paintAll(): void {
      paintInputs();
      paintResult();
      paintSaveControls();
    }

    function repaintInputs(): void {
      // Update only the visible values of each input box, not the help-pane state.
      (['fmv', 'nii', 'carryover', 'distYtd'] as const).forEach(key => {
        const input = root.querySelector<HTMLInputElement>(`[data-input-value="${key}"]`);
        if (input) {
          const display = inputs[key] === 0 ? '0' : Number(inputs[key]).toLocaleString('en-US');
          if (input.value !== display) input.value = display;
        }
      });
    }

    // ── Listeners ────────────────────────────────────────────────
    root.addEventListener('input', (e) => {
      const t = e.target as HTMLElement | null;
      const numInput = t?.closest('[data-input-value]') as HTMLInputElement | null;
      if (!numInput) return;
      const key = numInput.getAttribute('data-input-value') as keyof PayoutInputs | null;
      if (!key) return;
      const raw = numInput.value.replace(/[^\d]/g, '');
      const num = raw === '' ? 0 : Number(raw);
      inputs = { ...inputs, [key]: num };
      writeCachedInputs(inputs);
      // Re-format the visible input
      numInput.value = num === 0 ? '0' : num.toLocaleString('en-US');
      // Keep the caret at the end (after re-formatting). 99% of users edit at the end.
      numInput.setSelectionRange(numInput.value.length, numInput.value.length);
      paintResult();
      paintSaveControls();
    });

    root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const toggleBtn = t.closest('[data-action="toggle-help"]') as HTMLElement | null;
      if (toggleBtn) {
        const key = toggleBtn.getAttribute('data-help-key');
        if (!key) return;
        const body  = root.querySelector<HTMLElement>(`[data-help-body="${key}"]`);
        const icon  = root.querySelector<HTMLElement>(`[data-help-icon="${key}"]`);
        const label = root.querySelector<HTMLElement>(`[data-help-label="${key}"]`);
        if (!body) return;
        const isOpen = body.style.maxHeight && body.style.maxHeight !== '0px';
        body.style.maxHeight = isOpen ? '0' : '800px';
        if (icon)  icon.textContent  = isOpen ? '+' : '–';
        if (label) label.textContent = isOpen ? 'How do I find this number?' : 'Hide the walkthrough';
        return;
      }
      const saveBtnEl = t.closest('#po-save-btn') as HTMLElement | null;
      if (saveBtnEl) { void handleSave(); }
    });

    async function handleSave(): Promise<void> {
      saveState = 'saving'; errMsg = ''; paintSaveControls();
      try {
        const p = await window.api.savePayout(fiscalYearStart, inputs) as PayoutRow;
        if (!alive) return;
        lastSaved = p; saveState = 'idle';
      } catch (e) {
        if (!alive) return;
        errMsg = (e as Error).message || 'Save failed.';
        saveState = 'error';
      } finally {
        if (alive) paintSaveControls();
      }
    }

    // ── Initial paint + fetch ────────────────────────────────────
    paintAll();

    (async () => {
      try {
        const p = await window.api.getPayout(fiscalYearStart) as PayoutRow;
        if (!alive) return;
        inputs    = { fmv: p.fmv, nii: p.nii, carryover: p.carryover, distYtd: p.distYtd };
        lastSaved = p;
        loadState = 'ready';
        writeCachedInputs(inputs);
        repaintInputs();
        paintResult();
        paintSaveControls();
      } catch (err) {
        if (!alive) return;
        const e = err as { status?: number; message?: string };
        if (e.status === 404) {
          // No row yet — defaults stay, but the page is "ready".
          lastSaved = null;
          loadState = 'ready';
        } else {
          errMsg = e.message || 'Failed to load saved inputs.';
          loadState = 'error';
        }
        paintSaveControls();
      }
    })();

    (PayoutPage as Page & { _alive?: () => void })._alive = () => { alive = false; };
  },

  unmount(): void {
    const stop = (PayoutPage as Page & { _alive?: () => void })._alive;
    if (stop) stop();
  },
};

export default PayoutPage;
